import { Dealer } from './legos/dealerTypes';
import { UserRole, UserType } from './legos/constants';
import { allowedDeskDealRoles } from './legos/roleTypes';
// eslint-disable-next-line import/no-cycle
import { hasWebInStore } from './dealer-utils';
import { Applicant } from './legos/dealTypes';

/**
 * Checks if a user is locked out based on the `lockedUntil` property
 * @param user User to check
 * @returns boolean - True if locked out, false if not
 */
export const isUserLockedOut = (user: {
  lockedUntil?: Date | string | null;
}): boolean => {
  return user?.lockedUntil != null && new Date(user.lockedUntil) > new Date();
};

export const hasRole = (
  user: {
    roles?: UserRole[];
  },
  role: UserRole,
) => !!user?.roles?.includes?.(role);

export const isSalesPerson = (user: { roles?: UserRole[] }) =>
  (user?.roles?.length === 1 && hasRole(user, UserRole.USER)) ||
  (user?.roles?.length === 2 &&
    hasRole(user, UserRole.USER) &&
    hasRole(user, UserRole.SELF_DESKING));

export const isEmployeeAndAdmin = (user: {
  roles?: UserRole[];
  type: UserType;
}): boolean => {
  // all the upstart users will be admin so we can assume if userType === employee, they are admin
  // we need to add the admin check here for now before we address roles/permissions issue later
  return !!(
    user.type === UserType.EMPLOYEE && user.roles?.includes(UserRole.ADMIN)
  );
};

export const canDeskDeal = (user: {
  roles?: UserRole[];
  type: UserType;
}): boolean =>
  isEmployeeAndAdmin(user) ||
  !!user.roles?.some(role => allowedDeskDealRoles.includes(role));

export const isUser = (user: { roles?: UserRole[] }): boolean => {
  return !!user.roles?.includes(UserRole.USER);
};

// ARLCREDIT-517
export const canUserAccessFniPresentFlow = (
  user: {
    roles?: UserRole[];
    type: UserType;
  },
  dealer?: Dealer,
): boolean => {
  return isUser(user) && canDeskDeal(user) && hasWebInStore(dealer);
};

/**
 * Retrieves the full name of the current user.
 *
 * @param {Partial<User>} [currentUser] - The current user object, which may contain first and last name properties.
 * @returns {string | undefined} The full name of the current user, or undefined if the user object is not provided or does not contain name information.
 */
export const getFullNameFromCurrentUser = (currentUser?: {
  firstName?: string | null;
  lastName?: string | null;
}): string | undefined =>
  currentUser
    ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ')
    : undefined;

export const isValidUser = (user?: Applicant | null): boolean => {
  return (
    !!user &&
    !!user?.firstName &&
    !!user?.lastName &&
    (!!user?.email || !!user?.phone)
  );
};
